import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { clsx } from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { NavLink } from "react-router-dom";

import { Button } from "../../../components/atoms/Buttons/Button";
import { ToggleSwitch } from "../../../components/atoms/ToggleSwitch/ToggleSwitch";
import { smallInfo, subCheckmark } from "../../../designSystemIcons";
import { isChromeVersionUpToDate } from "../../../functions/isChromeVersionUpToDate";
import { sendMessageToExtension } from "../../../functions/sendMessageToExtension";
import { useInitImport } from "../../../hooks/useInitImport";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { setIsCurrentlyImporting } from "../../../redux/globalState/globalStateSlice";
import { addAlert } from "../../../redux/message/messageSlice";
import { AppDispatch } from "../../../redux/store";
import {
  fetchPlayerCount,
  fetchUserNotifications,
  getUserSelector,
  updateUser,
} from "../../../redux/user/userSlice";

const errorTypes = [
  "notUsingChrome",
  "noExtension",
  "noSubscription",
  "outdatedExtension",
  "notOnDesktop",
] as const;

type Error = (typeof errorTypes)[number];

const errorTexts: Record<(typeof errorTypes)[number], React.ReactNode> = {
  notUsingChrome:
    "You need to use Chrome to import players with our Chrome Extension.",
  noExtension: "You need to install our Chrome Extension to import players.",
  noSubscription: (
    <div className="flex flex-col">
      <p>You need an Elite or Ultimate subscription to import players.</p>
      <NavLink to={"/subscription"} className="underline text-primary-blue">
        See Subscriptions here
      </NavLink>
    </div>
  ),
  outdatedExtension:
    "Your extension is outdated. To update it completely close and reopen Chrome.",
  notOnDesktop:
    "You need to be on a desktop to import players with our Chrome Extension.",
} as const;

const ImportPlayersView = () => {
  const { user } = useSelector(getUserSelector);
  const [importing, setImporting] = useState(false);
  const [extensionVersion, setExtensionVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const initImport = useInitImport({ user });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<Error, boolean>>({
    notUsingChrome: false,
    noExtension: false,
    noSubscription: !localStorage.getItem("canUseClubImport"),
    outdatedExtension: false,
    notOnDesktop: false,
  });

  const sectionRef = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash?.startsWith("#sbc-storage")) {
      window.scrollTo({
        top: sectionRef.current.offsetTop,
        behavior: "smooth",
      });
    }
  }, [hash]);

  useEffect(() => {
    sendMessageToExtension({ user })
      .then((res) => {
        setErrors((prev) => ({
          ...prev,
          noExtension: res.msg !== "confirmation",
          outdatedExtension: !isChromeVersionUpToDate([2, 2, 20], res.version),
        }));
        setExtensionVersion(res.version);
      })
      .catch(() => setErrors((prev) => ({ ...prev, noExtension: true })));
  }, [user]);

  usePageVisibility(
    useCallback(
      (visible) => {
        if (visible && user && !importing) {
          if (!window?.chrome)
            return setErrors((prev) => ({ ...prev, notUsingChrome: true }));

          sendMessageToExtension({ user })
            .then((res) => {
              setErrors((prev) => ({
                ...prev,
                noExtension: res.msg !== "confirmation",
                outdatedExtension: !isChromeVersionUpToDate(
                  [2, 2, 20],
                  res.version,
                ),
              }));
              setExtensionVersion(res.version);
            })
            .catch(() => {
              setErrors((prev) => ({ ...prev, noExtension: true }));
            });
        }
      },
      [user, importing],
    ),
  );

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      noSubscription: !localStorage.getItem("canUseClubImport"),
      notUsingChrome: !window?.chrome,
      notOnDesktop: isMobile,
      outdatedExtension:
        !window?.chrome ||
        !extensionVersion ||
        !isChromeVersionUpToDate([2, 2, 20], extensionVersion),
    }));
  }, [user?.subscription, extensionVersion]);

  return (
    <div className="flex flex-col mx-auto text-xs gap-y-12 md-sm:text-sm touch-device:gap-y-16 touch-device:text-base">
      <div className="flex flex-col rounded-lg border border-white/10 bg-white/[1%] p-4 shadow-soft-dark-bottom touch-device:p-8">
        <div className="flex flex-col gap-y-4">
          <div className="flex justify-between">
            <h2 className="text-2xl font-medium">Club Import</h2>
            <div className="flex flex-col gap-y-2">
              <Button
                variant="tertiary"
                disabled={
                  errors.notUsingChrome ||
                  errors.noExtension ||
                  errors.noSubscription ||
                  errors.notOnDesktop ||
                  importing
                }
                disabledVariant="transparent"
                onClick={() =>
                  initImport().then(() => {
                    dispatch(setIsCurrentlyImporting(true));
                    window.open(
                      "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
                      "_blank",
                    );
                    setTimeout(() => {
                      setImporting(true);
                    }, 1000);
                  })
                }
              >
                Start Import
              </Button>
              <Button
                variant="tertiary"
                disabled={!importing}
                disabledVariant="transparent"
                onClick={() => {
                  setLoading(true);
                  sendMessageToExtension({ user, doneRequest: true })
                    .then(() => {
                      dispatch(fetchUserNotifications());
                      dispatch(fetchPlayerCount());
                      setImporting(false);
                      setLoading(false);
                      navigate("/my-club", { state: { importFinished: true } });
                    })
                    .catch(() =>
                      dispatch(
                        addAlert({
                          type: "error",
                          title: "Club import",
                          description: "Couldn't import players",
                        }),
                      ),
                    );
                }}
              >
                {loading ? <Oval height={"24px"} width={"24px"} /> : "Done"}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-y-5">
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <p className="text-white/75">
                  1. Use a browser with Chrome extensions on a desktop computer.
                </p>
                {!errors.notUsingChrome && !errors.notOnDesktop && subCheckmark}
              </div>
              {
                <p className="text-[#F94D6A] touch-device:pl-4">
                  {errors.notOnDesktop
                    ? errorTexts["notOnDesktop"]
                    : errors.notUsingChrome && errorTexts["notUsingChrome"]}
                </p>
              }
            </div>
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <p className="text-white/75">
                  2. Log in with an account with an active Elite or Ultimate
                  subscription.
                </p>
                {!errors.noSubscription && subCheckmark}
              </div>
              {errors.noSubscription && (
                <p className="text-[#F94D6A] touch-device:pl-4">
                  {errorTexts["noSubscription"]}
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <span className="text-white/75">
                  3. Make sure that you have the{" "}
                  <a
                    href="https://chromewebstore.google.com/detail/easysbc-player-import/mchecdiinfipdfihkoebfbpfnllbllhc?hl=en"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-primary-blue"
                  >
                    EasySBC Import Extension
                  </a>{" "}
                  downloaded.
                </span>
                {!errors.noExtension && subCheckmark}
              </div>
              {errors.noExtension && (
                <p className={clsx("text-[#F94D6A] touch-device:pl-4")}>
                  {errorTexts["noExtension"]}
                </p>
              )}
            </div>
            {!errors.notOnDesktop && (
              <div className="flex flex-col">
                <p className="text-white/75">
                  4. Follow the step below to import your players.
                </p>
                <div className="mt-3 flex flex-row justify-around rounded-xl bg-[#4A535C] py-4 min-w-[850px]">
                  <div className="w-40 px-3.5 pt-[9px] pb-[15px]">
                    <img
                      alt="club"
                      className="rounded-md shadow-soft-dark-bottom"
                      src={
                        import.meta.env.VITE_APP_ASSETS_BASE_URL +
                        "/misc/step_11.png"
                      }
                    />
                  </div>
                  <div className="w-40 px-3.5 pt-[9px] pb-[15px]">
                    <img
                      alt="club"
                      className="rounded shadow-soft-dark-bottom"
                      src={
                        import.meta.env.VITE_APP_ASSETS_BASE_URL +
                        "/misc/step_3.png"
                      }
                    />
                  </div>
                  <div className="w-40 px-3.5 pt-[9px] pb-[15px]">
                    <img
                      alt="club"
                      className="rounded-md shadow-soft-dark-bottom"
                      src={
                        import.meta.env.VITE_APP_ASSETS_BASE_URL +
                        "/misc/step_44.png"
                      }
                    />
                  </div>
                  <img
                    alt="import players"
                    className="-mr-5 w-44"
                    src={
                      import.meta.env.VITE_APP_ASSETS_BASE_URL +
                      "/misc/import_players.gif"
                    }
                  />
                  <div className="w-40 px-3.5 pt-[9px] pb-[15px]">
                    <img
                      alt="club"
                      className="rounded-md shadow-soft-dark-bottom"
                      src={
                        import.meta.env.VITE_APP_ASSETS_BASE_URL +
                        "/misc/step_66.png"
                      }
                    />
                  </div>
                </div>
                <p className="pl-4 my-1 italic text-white/50">
                  Or watch our video tutorial here:{" "}
                  <a
                    href="https://youtu.be/rI04nQPWM7c"
                    target="_blank"
                    rel="noreferrer"
                    className="italic underline opacity-50 text-primary-blue"
                  >
                    https://youtu.be/rI04nQPWM7c
                  </a>
                </p>
              </div>
            )}
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <span className="text-white/75">
                  Still having issues? Contact us on our{" "}
                  <a
                    href="https://discord.gg/easysbc-980827940902293525"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-primary-blue"
                  >
                    Discord server
                  </a>{" "}
                  to get help.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col rounded-lg border border-white/10 bg-white/[1%] p-4 shadow-soft-dark-bottom touch-device:p-8">
        <div className="flex flex-col gap-y-4">
          <div className="flex justify-between">
            <h2 className="text-2xl font-medium" ref={sectionRef}>
              Club Import with SBC Storage
            </h2>
            <div className="flex flex-col gap-y-2">
              <Button
                variant="tertiary"
                disabledVariant="transparent"
                disabled={
                  errors.notUsingChrome ||
                  errors.noExtension ||
                  errors.noSubscription ||
                  errors.outdatedExtension ||
                  errors.notOnDesktop ||
                  importing
                }
                onClick={() => {
                  initImport().then(() => {
                    dispatch(setIsCurrentlyImporting(true));
                    window.open(
                      "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
                      "_blank",
                    );
                    setTimeout(() => {
                      setImporting(true);
                    }, 1000);
                  });
                }}
              >
                Start Import
              </Button>
              <Button
                variant="tertiary"
                disabled={!importing}
                disabledVariant="transparent"
                onClick={() => {
                  setLoading(true);
                  sendMessageToExtension({ user, doneRequest: true })
                    .then(() => {
                      dispatch(fetchUserNotifications());
                      dispatch(fetchPlayerCount());
                      setImporting(false);
                      setLoading(false);
                      navigate("/my-club");
                    })
                    .catch(() =>
                      dispatch(
                        addAlert({
                          type: "error",
                          title: "Club import",
                          description: "Couldn't import players",
                        }),
                      ),
                    );
                }}
              >
                {loading ? <Oval height={"24px"} width={"24px"} /> : "Done"}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-y-5">
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <p className="text-white/75">
                  1. Use a browser with Chrome extensions on a desktop computer.
                </p>
                {!errors.notUsingChrome && !errors.notOnDesktop && subCheckmark}
              </div>

              <p className="text-[#F94D6A] touch-device:pl-4">
                {errors.notOnDesktop
                  ? errorTexts["notOnDesktop"]
                  : errors.notUsingChrome && errorTexts["notUsingChrome"]}
              </p>
            </div>
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <p className="text-white/75">
                  2. Log in with on an account with an active Elite or Ultimate
                  subscription.
                </p>
                {!errors.noSubscription && subCheckmark}
              </div>
              <p className="text-[#F94D6A] touch-device:pl-4">
                {errors.noSubscription && errorTexts["noSubscription"]}
              </p>
            </div>
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <p className="text-white/75">
                  3. Make sure that you have the{" "}
                  <a
                    href="https://chromewebstore.google.com/detail/easysbc-player-import/mchecdiinfipdfihkoebfbpfnllbllhc?hl=en"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-primary-blue"
                  >
                    EasySBC Import Extension
                  </a>{" "}
                  v.2.1.11 or above.
                </p>
                {!errors.noExtension &&
                  !errors.outdatedExtension &&
                  subCheckmark}
              </div>
              <p className={clsx("text-[#F94D6A] touch-device:pl-4")}>
                {errors.noExtension
                  ? errorTexts["noExtension"]
                  : errors.outdatedExtension &&
                    errorTexts["outdatedExtension"] +
                      ` Your current version is ${
                        extensionVersion ?? "unknown"
                      }.
                    `}
              </p>
            </div>
            {user?.uuid && (
              <div className="flex flex-col gap-y-1.5">
                <div className="flex gap-x-1">
                  <p className="text-white/75">
                    3. In order to keep your SBC storage up-to-date we highly
                    recommend to enable automatic import updates.
                  </p>
                  {user?.allowTracking && subCheckmark}
                </div>
                <div className="flex flex-col pl-4 gap-y-1">
                  <p className="pl-3 text-xs text-gray-400">Account Settings</p>
                  <div className="flex justify-between rounded-lg bg-white/10 p-3 touch-device:w-[488px]">
                    <div className="flex items-center gap-x-1">
                      <p>Allow automatic import updates</p>
                      <Tooltip
                        content={
                          <p className="w-64 text-white">
                            With automatic import updates, we track when players
                            enter or leave your club, when using the EA Web App.
                          </p>
                        }
                        side="top"
                        delayDuration={100}
                      >
                        <div className="p-2">{smallInfo}</div>
                      </Tooltip>
                    </div>
                    <ToggleSwitch
                      isActive={user?.allowTracking}
                      onChange={(isActive) => {
                        dispatch(
                          updateUser({ allowTracking: !user?.allowTracking }),
                        );
                        dispatch(
                          addAlert({
                            title:
                              (isActive ? "Enabled" : "Disabled") +
                              " email notifications",
                            description:
                              "You have successfully updated your email notification settings",
                            type: "success",
                          }),
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {!errors.notOnDesktop && (
              <div className="flex flex-col">
                <p className="mb-4 text-white/75">
                  4. Follow the steps shown in the video below to import your
                  players and SBC Storage
                </p>
                <video
                  className="rounded w-[650px]"
                  autoPlay={false}
                  muted
                  controls
                  title="Club Import With SBC Storage"
                >
                  <source
                    src={`${
                      import.meta.env.VITE_APP_ASSETS_BASE_URL
                    }/misc/sbc-storage2.mp4`}
                  />
                </video>
              </div>
            )}
            <div className="flex flex-col">
              <div className="flex gap-x-1">
                <span className="text-white/75">
                  Still having issues? Contact us on our{" "}
                  <a
                    href="https://discord.gg/easysbc-980827940902293525"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-primary-blue"
                  >
                    Discord server
                  </a>{" "}
                  to get help.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportPlayersView;
